import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackProductEvent } from "@/lib/productAnalytics";

export type DirectConnectedInvitation = {
  invitation_id: string;
  inviter_display_name: string;
  proposed_name: string;
  expires_at: string;
  match_date: string;
  match_time: string | null;
  court: string | null;
  surface: string | null;
  kind: string;
  set1_me: number | null;
  set1_opp: number | null;
  set1_tb_me: number | null;
  set1_tb_opp: number | null;
  set1_type: string | null;
  set2_me: number | null;
  set2_opp: number | null;
  set2_tb_me: number | null;
  set2_tb_opp: number | null;
  set2_type: string | null;
  set3_me: number | null;
  set3_opp: number | null;
  set3_tb_me: number | null;
  set3_tb_opp: number | null;
  set3_type: string | null;
};

const db = supabase as any;

/**
 * In-app inbox for Connected Match invitations explicitly targeted to the
 * signed-in user's already-linked Tennis Log identity. No public invite token is
 * exposed to the client.
 */
export function useDirectConnectedInvitations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["direct_connected_invitations", user?.id],
    enabled: !!user?.id,
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<DirectConnectedInvitation[]> => {
      const { data, error } = await db.rpc("get_my_direct_connected_match_invitations");
      if (error) {
        // Rollout-safe while the Phase 2G migration is not deployed yet.
        const message = String(error.message || "").toLowerCase();
        if (message.includes("does not exist") || message.includes("could not find the function")) return [];
        throw error;
      }
      return (data ?? []) as DirectConnectedInvitation[];
    },
  });
}

export function useCreateDirectConnectedInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (matchId: string) => {
      const { data, error } = await db.rpc("create_connected_match_direct_invitation", {
        p_source_match_id: matchId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, matchId) => {
      queryClient.invalidateQueries({ queryKey: ["match_invitation", matchId] });
      trackProductEvent("connected_invite_created", { delivery: "in_app" });
    },
  });
}

export function useClaimDirectConnectedInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await db.rpc("claim_direct_connected_match_invitation", {
        p_invitation_id: invitationId,
      });
      if (error) throw error;
      const row = (data ?? [])[0];
      return {
        sharedMatchId: row?.shared_match_id as string | undefined,
        matchId: row?.recipient_match_id as string | undefined,
        opponentId: row?.recipient_opponent_id as string | undefined,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["direct_connected_invitations"] });
      queryClient.invalidateQueries({ queryKey: ["matches"] });
      queryClient.invalidateQueries({ queryKey: ["opponents"] });
      trackProductEvent("connected_invite_claimed");
    },
  });
}
